import type { SchemaProp } from '@based'
import { html } from './shared.js'

const text = { type: 'string', localized: true } as const satisfies SchemaProp

// Top-level copy fields on globalSettings (no nesting). Kept as a const
// literal so the query builder can still narrow them as known keys when
// spread into the schema. Add new top-level translatable fields here.
export const TOP_LEVEL_COPY = {
  privacy: 'string',
  info: html,
} as const satisfies Record<string, SchemaProp>

type CopyField = string | { path: string; schema: SchemaProp }

export type CopyCategory = {
  category: string
  description: string
  fields: readonly CopyField[]
}

// Single source of truth: paths drive both the CMS UI categories AND the
// schema props on globalSettings (dictionary, terms, info, privacy).
// A bare string path defaults to a localized text field; pass an object to
// override (e.g. html-formatted body copy).
export const COPY_CATEGORIES = [
  {
    category: 'Sequences',
    description: 'All the sequences',
    fields: [],
  },
  {
    category: 'Onboarding',
    description: 'Onboarding page and steps',
    fields: [
      'dictionary.visitingFrom',
      'dictionary.nonParticipatingCountry',
      'dictionary.votingOnBehalf',
      'dictionary.tutorialMaxVotes',
      'dictionary.tutorialVotePrice',
      'dictionary.tutorialCardLimit',
      'dictionary.tutorialCallVoting',
      'dictionary.tutorialCallVotingCount',
      'dictionary.tutorialSMSVoting',
      'dictionary.tutorialSMSVotingCount',
      'dictionary.tutorialLostVotes',
      'dictionary.trackingTitle',
      'dictionary.trackingEssentials',
      'dictionary.trackingAnalytics',
      'dictionary.trackingPrivacyPolicy',
      'dictionary.acceptTracking',
      'dictionary.acceptEssentials',
      'dictionary.notAllowedToVote',
    ],
  },
  {
    category: 'Navigation',
    description: 'Header, footer and modal elements, page titles',
    fields: [
      'dictionary.localizationTitle',
      'dictionary.selectLanguage',
      'dictionary.recommended',
      'dictionary.allLanguages',
      'dictionary.selectVotingRegion',
      'dictionary.basedOnYourLocation',
      'dictionary.allVotingRegions',
      'dictionary.votingGroupTitle',
      'dictionary.legalGroupTitle',
      'dictionary.votingIntegrity',
      'dictionary.termsOfService',
      'dictionary.privacyPolicy',
      'dictionary.imprint',
      'dictionary.start',
      'dictionary.cart',
    ],
  },
  {
    category: 'General',
    description: 'Miscellaneous copy used on multiple pages',
    fields: [
      'dictionary.cardLocationNoticeOverseas',
      'dictionary.vote',
      'dictionary.votes',
      'dictionary.nOutOfMaxVotes',
      'dictionary.days',
      'dictionary.hours',
      'dictionary.minutes',
      'dictionary.seconds',
      'dictionary.cardLocationNotice',
      'dictionary.restOfTheWorld',
      'dictionary.onlyCallVotingSupported',
      'dictionary.onlySMSVotingSupported',
      'dictionary.backToStart',
      'dictionary.closeButton',
      'dictionary.continueButton',
    ],
  },
  {
    category: 'Placeholder pages',
    description: 'Countdown, voting is closed and voting will open soon pages',
    fields: ['dictionary.votingIsClosed'],
  },
  {
    category: 'Voting',
    description: 'Voting home page, prompts',
    fields: [
      'dictionary.votingPromptLong',
      'dictionary.votingPromptShort',
      'dictionary.votingReminder',
      'dictionary.voteButton',
    ],
  },
  {
    category: 'Cart',
    description: 'Online and call/SMS voting versions of the cart page',
    fields: [
      'dictionary.yourCart',
      'dictionary.emptyCart',
      'dictionary.total',
      'dictionary.emptyCartInstruction',
      'dictionary.addVotesButton',
      'dictionary.checkoutButton',
      'dictionary.callNowButton',
      'dictionary.sendSMSButton',
      'dictionary.paymentMethodsNotice',
      'dictionary.callNowDescription',
      'dictionary.sendSMSDescription',
      'dictionary.processingPayment',
    ],
  },
  {
    category: 'Checkout & payment',
    description: 'Checkout and payment in progress pages',
    fields: [
      'dictionary.checkout',
      'dictionary.disclaimers',
      'dictionary.noRefundNotice',
      'dictionary.extraFeeNotice',
      'dictionary.taxesIncludedNotice',
      'dictionary.selectPaymentMethod',
      'dictionary.allTransactionsSecured',
      'dictionary.contact',
      'dictionary.emailFieldLabel',
      'dictionary.termsCheckbox',
      'dictionary.newsCheckbox',
      'dictionary.payButton',
      'dictionary.processingMessage',
      'dictionary.processingLongWaitNotice',
    ],
  },
  {
    category: 'Error messages',
    description: 'All error messages related to payment',
    fields: [
      'dictionary.errorCaptcha',
      'dictionary.errorWrongRegion',
      'dictionary.errorCardAlreadyUsed',
      'dictionary.errorEmailAlreadyUsed',
      'dictionary.errorGeneric',
      'dictionary.errorInvalidEmail',
      'dictionary.errorInvalidEmailDomain',
    ],
  },
  {
    category: 'Thank you',
    description: 'Online and call/SMS voting versions of the thank you page',
    fields: [
      'dictionary.thankYouForVoting',
      'dictionary.playButton',
      'dictionary.nextButton',
      'dictionary.thankYouCallSMSVoteLimit',
      'dictionary.remainingVotesCallSMS',
      'dictionary.maxedOutNotice',
      'dictionary.voteAgainButton',
      'dictionary.voteForAnotherButton',
      'dictionary.thankYouCardLimit',
    ],
  },
  {
    category: 'Infra-down page',
    description: 'Copy for the infra-down page',
    fields: [
      'dictionary.infraDownTitle',
      'dictionary.infraDownDescription',
      'dictionary.refreshButton',
    ],
  },
  {
    category: 'Recap page',
    description: 'Copy for the recap video page',
    fields: [
      'dictionary.recapStartVideo',
      'dictionary.recapWatchVideo',
      'dictionary.recapProceedToVoting',
    ],
  },
  {
    category: 'Legal',
    description: 'Terms, voting integrity and imprint',
    fields: ['dictionary.moreInfoReachOut', { path: 'info', schema: html }],
  },
  {
    category: 'Terms',
    description: 'Terms of service content',
    fields: [
      'terms.title',
      'terms.introductionTitle',
      { path: 'terms.introductionText', schema: html },
      'terms.rulesTitle',
      { path: 'terms.rulesText', schema: html },
      'terms.procedureTitle',
      { path: 'terms.procedureText', schema: html },
      'terms.noRefundsTitle',
      { path: 'terms.noRefundsText', schema: html },
      'terms.dataTitle',
      { path: 'terms.dataText', schema: html },
      'terms.voterRespTitle',
      { path: 'terms.voterRespText', schema: html },
      'terms.votingWindowsTitle',
      'terms.votingRegionTitle',
      'terms.votingRegionDisclaimer',
      'terms.generalCondTitle',
      { path: 'terms.generalCondText', schema: html },
      'terms.jurisdictionTitle',
      { path: 'terms.jurisdictionText', schema: html },
      'terms.changesTitle',
      { path: 'terms.changesText', schema: html },
      { path: 'terms.lastUpdatedText', schema: html },
      'terms.firstSemiFinal',
      'terms.secondSemiFinal',
      'terms.grandFinal',
      'terms.preShow',
      'terms.liveShow',
      'terms.showStart',
      'terms.votingStart',
      'terms.votingEnd',
      'terms.afterLast',
      'terms.shortlyAfter',
      'terms.approx15After',
      'terms.approx30After',
      'terms.maxVotes',
      'terms.priceVote',
    ],
  },
] as const satisfies readonly CopyCategory[]

type Categories = typeof COPY_CATEGORIES
type CategoryName = Categories[number]['category']

type FieldPathOf<F> = F extends string
  ? F
  : F extends { path: infer P extends string }
    ? P
    : never

type DictKey<F> = FieldPathOf<F> extends `dictionary.${infer K}` ? K : never

export type DictionaryKeysFor<Name extends CategoryName> = DictKey<
  Extract<Categories[number], { category: Name }>['fields'][number]
>

const fieldPath = (f: CopyField): string => (typeof f === 'string' ? f : f.path)

const fieldSchema = (f: CopyField): SchemaProp => {
  if (typeof f !== 'string') return f.schema
  if (f in TOP_LEVEL_COPY) {
    return TOP_LEVEL_COPY[f as keyof typeof TOP_LEVEL_COPY]
  }
  return text
}

// Returns paths only — used by the CMS, which works with `string[]` fields.
export const getFieldPaths = (cat: CopyCategory): string[] =>
  cat.fields.map(fieldPath)

// Build object props for a nested namespace (e.g. 'dictionary', 'terms') by
// collecting every field whose path is `<prefix>.<key>`.
export const buildCopyProps = (prefix: string): Record<string, SchemaProp> => {
  const props: Record<string, SchemaProp> = {}
  for (const cat of COPY_CATEGORIES) {
    for (const f of cat.fields) {
      const parts = fieldPath(f).split('.')
      if (parts.length === 2 && parts[0] === prefix) {
        props[parts[1]] = fieldSchema(f)
      }
    }
  }
  return props
}
